import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { useAppDispatch } from "~/store/hooks";
import * as actions from "../actions";
import { messages } from "../i18n-messages";
import { isSsoRedirectSuppressed } from "../sso-redirect";

import { Button, TextInput } from "../../core";
import { messages as globalMessages } from "../../i18n-messages";
import { AuthenticationAPI, LocalStorageHelpers, SkyAPI } from "@ai360/core";

const { LoginState } = actions;

export const isValidEmail = (email: string): boolean => {
    const expression =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return new RegExp(expression).test(email);
};

export interface IRecoverPassword_Props {
    onError?: (error: Error) => void;
}

const RecoverPassword_ = (props: IRecoverPassword_Props): JSX.Element => {
    const { onError } = props;
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();

    const onResetLogin = () => dispatch(actions.resetLogin());
    const onSetLoginState = (loginState) => dispatch(actions.setLoginState(loginState));
    const onSetSecurityInfo = (email, question) =>
        dispatch(actions.setSecurityInfo(email, question));
    const onSetSecurityMessage = (code) => dispatch(actions.setSecurityMessage(code));
    const onStartProcessing = () => dispatch(actions.setProcessing(true));
    const onStopProcessing = () => dispatch(actions.setProcessing(false));

    const [email, setEmail] = useState<string>(
        LocalStorageHelpers.get(LocalStorageHelpers.USERNAME) || ""
    );
    const txtEmail = useRef<Record<string, any>>(null);

    const [triggerSsoStatus] = SkyAPI.useLazyGetSsoStatusQuery();
    const [triggerLastConnection] = SkyAPI.useLazyGetSsoLastConnectionQuery();

    function _handleError(error?: Error) {
        if (onError) {
            onError(error);
        }
        onStopProcessing();
        txtEmail.current?.input?.focus();
    }

    function _continueRecovery(recoveryEmail: string) {
        AuthenticationAPI.getSecurityQuestion(recoveryEmail)
            .then((question) => {
                if (question) {
                    onSetSecurityInfo(recoveryEmail, question);
                    onSetLoginState(LoginState.SECURITY_QUESTION);
                    onStopProcessing();
                } else {
                    AuthenticationAPI.firstTimeUserResetPassword(recoveryEmail)
                        .then((response) => {
                            if (response && typeof response.securityCode === "number") {
                                onSetSecurityMessage(response.securityCode);
                            } else {
                                _handleError(formatMessage(globalMessages.unknownError));
                            }
                        })
                        .catch((error) => {
                            const { apiResultObj } = error;
                            if (
                                apiResultObj &&
                                apiResultObj.model &&
                                typeof apiResultObj.model.securityCode === "number"
                            ) {
                                onSetSecurityMessage(apiResultObj.model.securityCode);
                            } else {
                                _handleError(formatMessage(messages.unableToResetPassword));
                            }
                        });
                }
            })
            .catch((error) => _handleError(error));
    }

    async function submit() {
        onStartProcessing();
        if (!email || !isValidEmail(email)) {
            _handleError(formatMessage(messages.validEmail));
            return;
        }
        try {
            const status = await triggerSsoStatus(email).unwrap();
            if (status?.ssoEnabled && status.status === SkyAPI.SsoConnectionStatus.AllConnected) {
                const last = await triggerLastConnection(email).unwrap();
                if (last?.redirectUrl) {
                    if (isSsoRedirectSuppressed()) {
                        console.warn("SSO redirect suppressed (local dev):", last.redirectUrl);
                    } else {
                        window.location.href = last.redirectUrl;
                        return;
                    }
                }
            }
        } catch {
            // fall through to normal recovery on lookup failure
        }
        _continueRecovery(email);
    }

    function _handleKeyDown(event) {
        if (event.key === "Enter") {
            submit();
        }
    }

    function cancel() {
        onResetLogin();
    }

    useEffect(() => {
        txtEmail.current?.input?.focus();
    }, []);

    return (
        <div>
            <div className="login-input-container">
                <div className="display-label">{formatMessage(messages.inputEmail)}</div>
                <TextInput
                    tabIndex={1}
                    value={email}
                    placeholderText={formatMessage(globalMessages.emailPlaceholder)}
                    onKeyDown={(e) => _handleKeyDown(e)}
                    onChange={(value) => setEmail(value)}
                    ref={(input) => {
                        txtEmail.current = input;
                    }}
                />
            </div>
            <div className="login-center">
                <Button
                    tabIndex={2}
                    className="login-btn"
                    type="submitTextOnly"
                    onClick={() => submit()}
                />
                <Button
                    tabIndex={3}
                    className="login-btn cancel-btn"
                    type="cancel"
                    onClick={() => cancel()}
                />
            </div>
        </div>
    );
};

export const RecoverPassword = RecoverPassword_;

export default RecoverPassword_;
